import {
  API_BASE_URL,
  getAuthHeaders,
  setLocalStorageWithSync,
} from '../../../../utils/utils';
import { getStoredUser } from '../../../../utils/authSession';

const createAuthAwareFetchError = (response, fallbackMessage) => {
  if (response?.status === 401) {
    return new Error('Authentication failed');
  }

  return new Error(fallbackMessage);
};

const resolveCurrentUserEmail = (userEmail) => {
  if (userEmail) {
    return userEmail;
  }

  return getStoredUser()?.email || null;
};

/**
 * PROGRESS DATA CACHING SYSTEM
 *
 * This module implements a comprehensive caching system for GRE progress data:
 *
 * CACHE TYPES:
 * - Quantitative Progress: liwaza_quant_progress_{userEmail} (1 hour cache)
 * - Verbal Progress: liwaza_verbal_progress_{userEmail} (1 hour cache)
 * - Reading Progress: liwaza_reading_progress_{userEmail} (10 minute cache)
 * - Reading Passages: liwaza_reading_passages_{userEmail}_{difficulty}_{topic}_{limit} (10 minute cache)
 *
 * CACHE BEHAVIOR:
 * - Quantitative/Verbal: 1 hour (3,600,000ms) automatic expiration
 * - Reading Comprehension: 10 minutes (600,000ms) automatic expiration
 * - Storage: localStorage (persistent across browser sessions)
 * - Validation: Automatic timestamp checking and cache invalidation
 * - User-specific: Each user has separate cache entries
 *
 * PERFORMANCE BENEFITS:
 * - First load: Backend fetch + automatic caching
 * - Subsequent loads: Instant localStorage retrieval (~60x faster)
 * - Reduced server load and bandwidth usage
 * - Reading data refreshes every 10 minutes for up-to-date content
 *
 * CACHE MANAGEMENT:
 * - clearProgressCache(): Clears all caches (backward compatibility)
 * - clearQuantProgressCache(userEmail): Clear specific user's quant cache
 * - clearVerbalProgressCache(userEmail): Clear specific user's verbal cache
 * - clearReadingProgressCache(userEmail): Clear specific user's reading progress cache
 * - clearReadingPassagesCache(userEmail): Clear specific user's reading passages cache
 * - clearAllReadingCaches(): Clear all reading comprehension caches
 * - getQuantProgressCacheInfo(userEmail): Get cache status for debugging
 * - getVerbalProgressCacheInfo(userEmail): Get verbal cache status
 *
 * AUTOMATIC INVALIDATION:
 * - Exercise completion (VocabularyArticle, etc.)
 * - Manual refresh buttons
 * - Time-based expiration (1 hour for progress, 10 minutes for reading)
 * - Error recovery scenarios
 *
 * READING COMPREHENSION SPECIFIC:
 * - Passages are cached with parameters (difficulty, topic, limit)
 * - Progress data is cached separately for faster loading
 * - 10-minute expiration ensures fresh content without overwhelming server
 */

// Cache management utilities
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds
const VERBAL_CACHE_SCHEMA = 2;
const READING_CACHE_DURATION = 10 * 60 * 1000; // 10 minutes in milliseconds for reading comprehension
const QUANT_CACHE_KEY_PREFIX = 'liwaza_quant_progress_';
const VERBAL_CACHE_KEY_PREFIX = 'liwaza_verbal_progress_';
const READING_CACHE_KEY_PREFIX = 'liwaza_reading_';
export const GRE_VERBAL_TIPS_READ_STATUS_KEY = 'gre_verbal_tips_read_status';
export const GRE_VERBAL_TIPS_VERSION = '2026-03-official-results-techniques-v1';
export const GRE_VERBAL_TIPS_ANNOUNCEMENT =
  'Based on recent official results from some of our members and learners, here are the new techniques working now and worth using from now on.';

const getStoredVerbalTipsReadStatus = () => {
  try {
    const stored = localStorage.getItem(GRE_VERBAL_TIPS_READ_STATUS_KEY);
    if (!stored) return {};
    const parsed = JSON.parse(stored);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    console.error('Error reading GRE verbal tips status:', error);
    return {};
  }
};

export const hasReadGreVerbalTips = (section) => {
  if (!section) return false;
  const stored = getStoredVerbalTipsReadStatus();
  const sectionStatus = stored?.[section];

  return (
    sectionStatus?.read === true &&
    sectionStatus?.version === GRE_VERBAL_TIPS_VERSION
  );
};

export const markGreVerbalTipsAsRead = async (section) => {
  if (!section) return false;

  try {
    const stored = getStoredVerbalTipsReadStatus();
    const nextStatus = {
      ...stored,
      [section]: {
        read: true,
        version: GRE_VERBAL_TIPS_VERSION,
        readAt: new Date().toISOString(),
      },
    };

    await setLocalStorageWithSync(
      GRE_VERBAL_TIPS_READ_STATUS_KEY,
      JSON.stringify(nextStatus)
    );
    return true;
  } catch (error) {
    console.error('Error marking GRE verbal tips as read:', error);
    return false;
  }
};

// Cache utilities for Quantitative
const getCacheKey = (userEmail) => `${QUANT_CACHE_KEY_PREFIX}${userEmail}`;
const getVerbalCacheKey = (userEmail) =>
  `${VERBAL_CACHE_KEY_PREFIX}${userEmail}`;
const getReadingProgressCacheKey = (userEmail) =>
  `${READING_CACHE_KEY_PREFIX}progress_${userEmail}`;
const getReadingPassagesCacheKey = (userEmail, difficulty, topic, limit) =>
  `${READING_CACHE_KEY_PREFIX}passages_${userEmail}_${difficulty || 'all'}_${topic || 'all'}_${limit || 20}`;

const getCachedData = (userEmail) => {
  try {
    const cacheKey = getCacheKey(userEmail);
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is expired (older than 1 hour)
    if (now - timestamp > CACHE_DURATION) {
      localStorage.removeItem(cacheKey);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error reading cached quant progress data:', error);
    return null;
  }
};

// Cache utilities for Verbal
const getCachedVerbalData = (userEmail) => {
  try {
    const cacheKey = getVerbalCacheKey(userEmail);
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    const { data, timestamp, schema } = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is expired (older than 1 hour) or predates struggle spots.
    if (schema !== VERBAL_CACHE_SCHEMA || now - timestamp > CACHE_DURATION) {
      localStorage.removeItem(cacheKey);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error reading cached verbal progress data:', error);
    return null;
  }
};

// Cache utilities for Reading Comprehension (10-minute expiration)
const getCachedReadingProgress = (userEmail) => {
  try {
    const cacheKey = getReadingProgressCacheKey(userEmail);
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is expired (older than 10 minutes)
    if (now - timestamp > READING_CACHE_DURATION) {
      localStorage.removeItem(cacheKey);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error reading cached reading progress data:', error);
    return null;
  }
};

const getCachedReadingPassages = (userEmail, difficulty, topic, limit) => {
  try {
    const cacheKey = getReadingPassagesCacheKey(
      userEmail,
      difficulty,
      topic,
      limit
    );
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is expired (older than 10 minutes)
    if (now - timestamp > READING_CACHE_DURATION) {
      localStorage.removeItem(cacheKey);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error reading cached reading passages data:', error);
    return null;
  }
};

const setCachedData = (userEmail, data) => {
  try {
    const cacheKey = getCacheKey(userEmail);
    const cacheData = {
      data,
      timestamp: Date.now(),
      userEmail,
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    console.log(`Cached quantitative progress data for ${userEmail}`);
  } catch (error) {
    console.error('Error caching quant progress data:', error);
  }
};

const setCachedVerbalData = (userEmail, data) => {
  try {
    const cacheKey = getVerbalCacheKey(userEmail);
    const cacheData = {
      data,
      timestamp: Date.now(),
      schema: VERBAL_CACHE_SCHEMA,
      userEmail,
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    console.log(`Cached verbal progress data for ${userEmail}`);
  } catch (error) {
    console.error('Error caching verbal progress data:', error);
  }
};

const setCachedReadingProgress = (userEmail, data) => {
  try {
    const cacheKey = getReadingProgressCacheKey(userEmail);
    const cacheData = {
      data,
      timestamp: Date.now(),
      userEmail,
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    console.log(`Cached reading progress data for ${userEmail}`);
  } catch (error) {
    console.error('Error caching reading progress data:', error);
  }
};

const setCachedReadingPassages = (
  userEmail,
  difficulty,
  topic,
  limit,
  data
) => {
  try {
    const cacheKey = getReadingPassagesCacheKey(
      userEmail,
      difficulty,
      topic,
      limit
    );
    const cacheData = {
      data,
      timestamp: Date.now(),
      userEmail,
      difficulty,
      topic,
      limit,
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    console.log(
      `Cached reading passages data for ${userEmail} (${difficulty || 'all'}, ${topic || 'all'}, ${limit || 20})`
    );
  } catch (error) {
    console.error('Error caching reading passages data:', error);
  }
};

const clearCachedData = (userEmail) => {
  try {
    const cacheKey = getCacheKey(userEmail);
    localStorage.removeItem(cacheKey);
    console.log(`Cleared cached quantitative progress data for ${userEmail}`);
  } catch (error) {
    console.error('Error clearing cached quant progress data:', error);
  }
};

const clearCachedVerbalData = (userEmail) => {
  try {
    const cacheKey = getVerbalCacheKey(userEmail);
    localStorage.removeItem(cacheKey);
    const normalizedEmail = String(userEmail || '').toLowerCase();
    if (normalizedEmail) {
      cachedProgressDataByUser.delete(normalizedEmail);
      cachedProgressTimestampByUser.delete(normalizedEmail);
      ongoingProgressRequestByUser.delete(normalizedEmail);
    }
    console.log(`Cleared cached verbal progress data for ${userEmail}`);
  } catch (error) {
    console.error('Error clearing cached verbal progress data:', error);
  }
};

const clearAllQuantCaches = () => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith(QUANT_CACHE_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
    console.log('Cleared all quantitative progress caches');
  } catch (error) {
    console.error('Error clearing all quant caches:', error);
  }
};

const clearAllVerbalCaches = () => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith(VERBAL_CACHE_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
    cachedProgressDataByUser.clear();
    cachedProgressTimestampByUser.clear();
    ongoingProgressRequestByUser.clear();
    console.log('Cleared all verbal progress caches');
  } catch (error) {
    console.error('Error clearing all verbal caches:', error);
  }
};

const clearCachedReadingProgress = (userEmail) => {
  try {
    const cacheKey = getReadingProgressCacheKey(userEmail);
    localStorage.removeItem(cacheKey);
    console.log(`Cleared cached reading progress data for ${userEmail}`);
  } catch (error) {
    console.error('Error clearing cached reading progress data:', error);
  }
};

const clearCachedReadingPassages = (
  userEmail,
  difficulty = null,
  topic = null,
  limit = null
) => {
  try {
    if (difficulty !== null && topic !== null && limit !== null) {
      // Clear specific cache
      const cacheKey = getReadingPassagesCacheKey(
        userEmail,
        difficulty,
        topic,
        limit
      );
      localStorage.removeItem(cacheKey);
      console.log(`Cleared specific reading passages cache for ${userEmail}`);
    } else {
      // Clear all reading passages caches for this user
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (
          key.startsWith(`${READING_CACHE_KEY_PREFIX}passages_${userEmail}_`)
        ) {
          localStorage.removeItem(key);
        }
      });
      console.log(`Cleared all reading passages caches for ${userEmail}`);
    }
  } catch (error) {
    console.error('Error clearing cached reading passages data:', error);
  }
};

const clearAllReadingCaches = () => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith(READING_CACHE_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
    console.log('Cleared all reading comprehension caches');
  } catch (error) {
    console.error('Error clearing all reading caches:', error);
  }
};

// Core API function to get comprehensive verbal progress with caching
export const getComprehensiveVerbalProgress = async (
  userEmail,
  forceRefresh = false
) => {
  try {
    if (!userEmail) {
      throw new Error('User email not found');
    }

    if (forceRefresh) {
      clearCachedVerbalData(userEmail);
    }

    // Check cache first
    const cachedData = getCachedVerbalData(userEmail);
    if (cachedData) {
      console.log(`Using cached verbal progress data for ${userEmail}`);
      return cachedData;
    }

    // Fetch from backend if no valid cache
    console.log(`Fetching fresh verbal progress data for ${userEmail}`);
    const response = await fetch(`${API_BASE_URL}/api/gre/verbal/progress`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw createAuthAwareFetchError(
        response,
        'Failed to fetch comprehensive verbal progress'
      );
    }

    const data = await response.json();

    // Cache the fresh data
    setCachedVerbalData(userEmail, data);

    return data;
  } catch (error) {
    console.error('Error fetching comprehensive verbal progress:', error);
    throw error;
  }
};

export const getChallengeWords = async () => {
  const response = await fetch(`${API_BASE_URL}/api/gre/verbal/challenge-words`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw createAuthAwareFetchError(
      response,
      'Failed to fetch vocabulary challenge words'
    );
  }
  return response.json();
};

// Core API function to get comprehensive quantitative progress
export const getComprehensiveQuantProgress = async (userEmail) => {
  try {
    if (!userEmail) {
      throw new Error('User email not found');
    }

    // Check cache first
    const cachedData = getCachedData(userEmail);
    if (cachedData) {
      console.log(`Using cached quantitative progress data for ${userEmail}`);
      return cachedData;
    }

    // Fetch from backend if no valid cache
    console.log(`Fetching fresh quantitative progress data for ${userEmail}`);
    const response = await fetch(`${API_BASE_URL}/api/gre/quant/progress`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw createAuthAwareFetchError(
        response,
        'Failed to fetch comprehensive quantitative progress'
      );
    }

    const data = await response.json();

    // Cache the fresh data
    setCachedData(userEmail, data);

    return data;
  } catch (error) {
    console.error('Error fetching comprehensive quantitative progress:', error);
    throw error;
  }
};

// Helper functions to extract specific component data from comprehensive progress

// Vocabulary Mastery Progress
export const getVocabularyProgressFromComprehensive = (comprehensiveData) => {
  if (comprehensiveData.vocabulary_mastery) {
    return {
      totalWords: comprehensiveData.vocabulary_mastery.total_words_encountered,
      masteredWords: comprehensiveData.vocabulary_mastery.total_words_mastered,
      accuracy: comprehensiveData.vocabulary_mastery.overall_accuracy,
      streak: comprehensiveData.overall_summary?.current_streak || 0,
      lastUpdated: comprehensiveData.overall_summary?.last_activity_date,
      masteredWordsList:
        comprehensiveData.vocabulary_mastery.mastered_words || [],
      wordsNeedingPractice:
        comprehensiveData.vocabulary_mastery.words_needing_practice || [],
      weeklyProgress:
        comprehensiveData.vocabulary_mastery.weekly_progress || {},
      monthlyProgress:
        comprehensiveData.vocabulary_mastery.monthly_progress || {},
      recentArticles:
        comprehensiveData.vocabulary_mastery.recent_articles || [],
      completedArticlesCount:
        comprehensiveData.vocabulary_mastery.completed_articles_count ||
        comprehensiveData.vocabulary_mastery.articles_read ||
        0,
      masteryRate: comprehensiveData.vocabulary_mastery.mastery_rate || 0,
      wordsInProgress:
        comprehensiveData.vocabulary_mastery.words_in_progress || 0,
      wordsNeedReview:
        comprehensiveData.vocabulary_mastery.words_need_review || 0,
      targetWords: comprehensiveData.vocabulary_mastery.target_words || 800,
      completionPercentage:
        comprehensiveData.vocabulary_mastery.completion_percentage || 0,
      averageWPM: comprehensiveData.vocabulary_mastery.average_wpm || 0,
      totalReadingTime:
        comprehensiveData.vocabulary_mastery.total_reading_time || 0,
      articlesRead: comprehensiveData.vocabulary_mastery.articles_read || 0,
      readinessScore: comprehensiveData.vocabulary_mastery.readiness_score || 0,
    };
  }

  // Fallback to flat structure for backward compatibility
  return {
    totalWords: comprehensiveData.total_words_encountered || 0,
    masteredWords: comprehensiveData.total_words_mastered || 0,
    accuracy: comprehensiveData.overall_accuracy || 0,
    streak: comprehensiveData.current_streak || 0,
    lastUpdated: comprehensiveData.last_activity_date,
    masteredWordsList: comprehensiveData.mastered_words || [],
    wordsNeedingPractice: comprehensiveData.words_needing_practice || [],
    weeklyProgress: comprehensiveData.weekly_progress || {},
    monthlyProgress: comprehensiveData.monthly_progress || {},
    recentArticles: [],
    completedArticlesCount: 0,
    masteryRate: 0,
    wordsInProgress: 0,
    wordsNeedReview: 0,
    targetWords: 1000,
    completionPercentage: 0,
    averageWPM: 0,
    totalReadingTime: 0,
    articlesRead: 0,
  };
};

// Text Completion Progress
export const getTextCompletionProgressFromComprehensive = (
  comprehensiveData
) => {
  if (comprehensiveData.text_completion) {
    return {
      totalQuestions:
        comprehensiveData.text_completion.total_questions_attempted || 0,
      correctAnswers:
        comprehensiveData.text_completion.total_questions_correct || 0,
      accuracy: comprehensiveData.text_completion.overall_accuracy || 0,
      questionsByDifficulty:
        comprehensiveData.text_completion.questions_by_difficulty || {},
      questionsByBlanks:
        comprehensiveData.text_completion.questions_by_blanks || {},
      averageTimePerQuestion:
        comprehensiveData.text_completion.average_time_per_question || 0,
      targetAccuracy: comprehensiveData.text_completion.target_accuracy || 85.0,
      improvementAreas:
        comprehensiveData.text_completion.improvement_areas || [],
      recentSessions: comprehensiveData.text_completion.recent_sessions || [],
      readinessScore: comprehensiveData.text_completion.readiness_score || 0,
      lastUpdated: comprehensiveData.overall_summary?.last_activity_date,
    };
  }

  return {
    totalQuestions: 0,
    correctAnswers: 0,
    accuracy: 0,
    questionsByDifficulty: {},
    questionsByBlanks: {},
    averageTimePerQuestion: 0,
    targetAccuracy: 85.0,
    improvementAreas: [],
    recentSessions: [],
    readinessScore: 0,
    lastUpdated: null,
  };
};

// Sentence Equivalence Progress
export const getSentenceEquivalenceProgressFromComprehensive = (
  comprehensiveData
) => {
  if (comprehensiveData.sentence_equivalence) {
    return {
      totalQuestions:
        comprehensiveData.sentence_equivalence.total_questions_attempted || 0,
      correctAnswers:
        comprehensiveData.sentence_equivalence.total_questions_correct || 0,
      accuracy: comprehensiveData.sentence_equivalence.overall_accuracy || 0,
      questionsByDifficulty:
        comprehensiveData.sentence_equivalence.questions_by_difficulty || {},
      wordPairsMastered:
        comprehensiveData.sentence_equivalence.common_word_pairs_mastered || 0,
      targetWordPairs:
        comprehensiveData.sentence_equivalence.target_word_pairs || 100,
      averageTimePerQuestion:
        comprehensiveData.sentence_equivalence.average_time_per_question || 0,
      targetAccuracy:
        comprehensiveData.sentence_equivalence.target_accuracy || 80.0,
      improvementAreas:
        comprehensiveData.sentence_equivalence.improvement_areas || [],
      recentSessions:
        comprehensiveData.sentence_equivalence.recent_sessions || [],
      readinessScore:
        comprehensiveData.sentence_equivalence.readiness_score || 0,
      lastUpdated: comprehensiveData.overall_summary?.last_activity_date,
    };
  }

  return {
    totalQuestions: 0,
    correctAnswers: 0,
    accuracy: 0,
    questionsByDifficulty: {},
    wordPairsMastered: 0,
    targetWordPairs: 100,
    averageTimePerQuestion: 0,
    targetAccuracy: 80.0,
    improvementAreas: [],
    recentSessions: [],
    readinessScore: 0,
    lastUpdated: null,
  };
};

// Reading Comprehension Progress
export const getReadingComprehensionProgressFromComprehensive = (
  comprehensiveData
) => {
  if (comprehensiveData.reading_comprehension) {
    return {
      totalPassages:
        comprehensiveData.reading_comprehension.total_passages_completed || 0,
      totalQuestions:
        comprehensiveData.reading_comprehension.total_questions_attempted || 0,
      correctAnswers:
        comprehensiveData.reading_comprehension.total_questions_correct || 0,
      accuracy: comprehensiveData.reading_comprehension.overall_accuracy || 0,
      averageReadingSpeed:
        comprehensiveData.reading_comprehension.average_reading_speed_wpm || 0,
      targetReadingSpeed:
        comprehensiveData.reading_comprehension.target_reading_speed_wpm || 350,
      passagesByType:
        comprehensiveData.reading_comprehension.passages_by_type || {},
      questionsByType:
        comprehensiveData.reading_comprehension.questions_by_type || {},
      averageTimePerPassage:
        comprehensiveData.reading_comprehension.average_time_per_passage || 0,
      targetTimePerPassage:
        comprehensiveData.reading_comprehension.target_time_per_passage || 420,
      targetAccuracy:
        comprehensiveData.reading_comprehension.target_accuracy || 75.0,
      improvementAreas:
        comprehensiveData.reading_comprehension.improvement_areas || [],
      recentSessions:
        comprehensiveData.reading_comprehension.recent_sessions || [],
      readinessScore:
        comprehensiveData.reading_comprehension.readiness_score || 0,
      lastUpdated: comprehensiveData.overall_summary?.last_activity_date,
    };
  }

  return {
    totalPassages: 0,
    totalQuestions: 0,
    correctAnswers: 0,
    accuracy: 0,
    averageReadingSpeed: 0,
    targetReadingSpeed: 350,
    passagesByType: {},
    questionsByType: {},
    averageTimePerPassage: 0,
    targetTimePerPassage: 420,
    targetAccuracy: 75.0,
    improvementAreas: [],
    recentSessions: [],
    readinessScore: 0,
    lastUpdated: null,
  };
};

// Overall Summary
export const getOverallSummaryFromComprehensive = (comprehensiveData) => {
  if (comprehensiveData.overall_summary) {
    return {
      totalStudyTime:
        comprehensiveData.overall_summary.total_study_time_minutes || 0,
      currentStreak: comprehensiveData.overall_summary.current_streak || 0,
      lastActivityDate: comprehensiveData.overall_summary.last_activity_date,
      overallReadinessScore:
        comprehensiveData.overall_summary.overall_readiness_score || 0,
      targetScoreRange:
        comprehensiveData.overall_summary.target_score_range || '160+',
      estimatedVerbalScore:
        comprehensiveData.overall_summary.estimated_verbal_score || 0,
    };
  }

  return {
    totalStudyTime: 0,
    currentStreak: comprehensiveData.current_streak || 0,
    lastActivityDate: comprehensiveData.last_activity_date,
    overallReadinessScore: 0,
    targetScoreRange: '160+',
    estimatedVerbalScore: 0,
  };
};

// Performance Analytics
export const getPerformanceAnalyticsFromComprehensive = (comprehensiveData) => {
  if (comprehensiveData.performance_analytics) {
    return {
      strengths: comprehensiveData.performance_analytics.strengths || [],
      areasForImprovement:
        comprehensiveData.performance_analytics.areas_for_improvement || [],
      studyRecommendations:
        comprehensiveData.performance_analytics.study_recommendations || [],
      recommendations:
        comprehensiveData.performance_analytics.recommendations || [],
      projectedScoreImprovement:
        comprehensiveData.performance_analytics.projected_score_improvement ||
        {},
    };
  }

  return {
    strengths: [],
    areasForImprovement: [],
    studyRecommendations: [],
    recommendations: [],
    projectedScoreImprovement: {},
  };
};

export const getStruggleSpotsFromComprehensive = (comprehensiveData) => {
  const spots = comprehensiveData?.struggle_spots || {};
  const counts = spots.practice_counts || {};
  return {
    words: spots.words || [],
    readingSkills: spots.reading_skills || [],
    passageTypes: spots.passage_types || [],
    other: spots.other || [],
    practiceCounts: {
      textCompletion: counts.text_completion || 0,
      sentenceEquivalence: counts.sentence_equivalence || 0,
      readingQuestions: counts.reading_questions || 0,
    },
  };
};

export const getReadinessAnalyticsFromComprehensive = (comprehensiveData) => {
  if (!comprehensiveData?.readiness_analytics) {
    return null;
  }
  const analytics = comprehensiveData.readiness_analytics;
  return {
    overallScore: analytics.overall_score ?? 0,
    level: analytics.level || '',
    confidence: analytics.confidence || 'none',
    breakdown: analytics.breakdown || {},
    components: analytics.components || {},
    areasToImprove: analytics.areas_to_improve || [],
    scorePrediction: analytics.score_prediction || {},
  };
};

const VERBAL_RECOMMENDATION_COMPONENT_MAP = {
  text_completion: 'textcompletion',
  sentence_equivalence: 'sentence',
  reading_comprehension: 'reading',
  reading: 'reading',
  vocabulary: 'vocabularymastery',
};

export const mapBackendVerbalRecommendations = (comprehensiveData) => {
  const perf = getPerformanceAnalyticsFromComprehensive(comprehensiveData);
  if (perf.recommendations?.length) {
    return perf.recommendations.map((rec) => ({
      priority: rec.priority || 'medium',
      component: rec.title,
      message: rec.description,
      action: rec.title,
      route: '/exams/gre/',
      activeComponent:
        VERBAL_RECOMMENDATION_COMPONENT_MAP[rec.action_target] ||
        'vocabularymastery',
    }));
  }
  return null;
};

// Study Patterns
export const getStudyPatternsFromComprehensive = (comprehensiveData) => {
  if (comprehensiveData.study_patterns) {
    return {
      mostActiveDay: comprehensiveData.study_patterns.most_active_day || '',
      averageSessionDuration:
        comprehensiveData.study_patterns.average_session_duration || 0,
      preferredStudyTime:
        comprehensiveData.study_patterns.preferred_study_time || '',
      consistencyScore: comprehensiveData.study_patterns.consistency_score || 0,
      totalStudySessions:
        comprehensiveData.study_patterns.total_study_sessions || 0,
      weeklyStudyFrequency:
        comprehensiveData.study_patterns.weekly_study_frequency || 0,
    };
  }

  return {
    mostActiveDay: '',
    averageSessionDuration: 0,
    preferredStudyTime: '',
    consistencyScore: 0,
    totalStudySessions: 0,
    weeklyStudyFrequency: 0,
  };
};

// Cached progress data with request deduplication
const cachedProgressDataByUser = new Map();
const cachedProgressTimestampByUser = new Map();
const ongoingProgressRequestByUser = new Map();

export const getCachedComprehensiveProgress = async (
  userEmail,
  forceRefresh = false
) => {
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  const now = Date.now();
  const normalizedEmail = String(userEmail || '').toLowerCase();

  if (!normalizedEmail) {
    throw new Error('User email not found');
  }

  const cachedProgressData = cachedProgressDataByUser.get(normalizedEmail);
  const cachedProgressTimestamp =
    cachedProgressTimestampByUser.get(normalizedEmail);

  // Return cached data if available and not expired
  if (
    !forceRefresh &&
    cachedProgressData &&
    cachedProgressTimestamp &&
    now - cachedProgressTimestamp < CACHE_DURATION
  ) {
    return cachedProgressData;
  }

  // If request is already in progress, return the existing promise
  const ongoingProgressRequest =
    ongoingProgressRequestByUser.get(normalizedEmail);
  if (ongoingProgressRequest) {
    return ongoingProgressRequest;
  }

  // Create new request
  const request = getComprehensiveVerbalProgress(userEmail)
    .then((data) => {
      cachedProgressDataByUser.set(normalizedEmail, data);
      cachedProgressTimestampByUser.set(normalizedEmail, now);
      return data;
    })
    .finally(() => {
      ongoingProgressRequestByUser.delete(normalizedEmail);
    });

  ongoingProgressRequestByUser.set(normalizedEmail, request);
  return request;
};

/**
 * Sync flashcard mastery to the backend so it counts toward verbal progress.
 * Now delegates to the dedicated flashcard mastery API endpoint.
 */
export const reportFlashcardMasteryToBackend = async (userEmail) => {
  if (!userEmail || userEmail === 'default') return;
  try {
    const { fetchMastery } = await import('./flashcardAPI');
    await fetchMastery(userEmail);
    clearCachedVerbalData(userEmail);
  } catch (e) {
    console.error('Error reporting flashcard mastery:', e);
  }
};

// Export cache management functions for external use
export const clearQuantProgressCache = clearCachedData;
export const clearVerbalProgressCache = clearCachedVerbalData;
export const clearAllQuantProgressCaches = clearAllQuantCaches;
export const clearAllVerbalProgressCaches = clearAllVerbalCaches;
export const clearReadingProgressCache = clearCachedReadingProgress;
export const clearReadingPassagesCache = clearCachedReadingPassages;
export { clearAllReadingCaches };

// Cached Reading Comprehension API functions (10-minute cache)
export const getCachedReadingComprehensionProgress = async (userEmail) => {
  try {
    const normalizedUserEmail = resolveCurrentUserEmail(userEmail);

    if (!normalizedUserEmail) {
      throw new Error('User email not found');
    }

    // Check cache first
    const cachedData = getCachedReadingProgress(normalizedUserEmail);
    if (cachedData) {
      console.log(
        `Using cached reading progress data for ${normalizedUserEmail}`
      );
      return cachedData;
    }

    // Fetch from backend if no valid cache
    console.log(
      `Fetching fresh reading progress data for ${normalizedUserEmail}`
    );
    const response = await fetch(`${API_BASE_URL}/api/gre/reading/progress`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw createAuthAwareFetchError(
        response,
        'Failed to fetch reading comprehension progress'
      );
    }

    const data = await response.json();

    // Cache the fresh data
    setCachedReadingProgress(normalizedUserEmail, data);

    return data;
  } catch (error) {
    console.error('Error fetching reading comprehension progress:', error);
    throw error;
  }
};

export const getCachedReadingComprehensionPassages = async (
  userEmail,
  difficulty = null,
  topic = null,
  limit = 100
) => {
  try {
    const normalizedUserEmail = resolveCurrentUserEmail(userEmail);

    if (!normalizedUserEmail) {
      throw new Error('User email not found');
    }

    // Check cache first
    const cachedData = getCachedReadingPassages(
      normalizedUserEmail,
      difficulty,
      topic,
      limit
    );
    if (cachedData) {
      console.log(
        `Using cached reading passages data for ${normalizedUserEmail} (${difficulty || 'all'}, ${topic || 'all'}, ${limit})`
      );
      return cachedData;
    }

    // Fetch from backend if no valid cache
    console.log(
      `Fetching fresh reading passages data for ${normalizedUserEmail} (${difficulty || 'all'}, ${topic || 'all'}, ${limit})`
    );
    const params = new URLSearchParams();
    if (difficulty) params.append('difficulty', difficulty);
    if (topic) params.append('topic', topic);
    params.append('limit', limit.toString());

    const response = await fetch(
      `${API_BASE_URL}/api/gre/exercises/reading-comprehension?${params}`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw createAuthAwareFetchError(
        response,
        'Failed to fetch reading comprehension passages'
      );
    }

    const data = await response.json();

    // Cache the fresh data
    setCachedReadingPassages(
      normalizedUserEmail,
      difficulty,
      topic,
      limit,
      data
    );

    return data;
  } catch (error) {
    console.error('Error fetching reading comprehension passages:', error);
    throw error;
  }
};

// Backward compatibility: Clear both verbal and quantitative caches
export const clearProgressCache = () => {
  try {
    clearAllQuantCaches();
    clearAllVerbalCaches();
    clearAllReadingCaches();
    console.log(
      'Cleared all progress caches (verbal + quantitative + reading)'
    );
  } catch (error) {
    console.error('Error clearing all progress caches:', error);
  }
};

export const getQuantProgressCacheInfo = (userEmail) => {
  try {
    const cacheKey = getCacheKey(userEmail);
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return { cached: false };

    const { timestamp } = JSON.parse(cached);
    const now = Date.now();
    const age = now - timestamp;
    const isExpired = age > CACHE_DURATION;

    return {
      cached: true,
      timestamp,
      age,
      isExpired,
      expiresIn: CACHE_DURATION - age,
      type: 'quantitative',
    };
  } catch (error) {
    console.error('Error getting quant cache info:', error);
    return { cached: false, error: error.message };
  }
};

export const getVerbalProgressCacheInfo = (userEmail) => {
  try {
    const cacheKey = getVerbalCacheKey(userEmail);
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return { cached: false };

    const { timestamp } = JSON.parse(cached);
    const now = Date.now();
    const age = now - timestamp;
    const isExpired = age > CACHE_DURATION;

    return {
      cached: true,
      timestamp,
      age,
      isExpired,
      expiresIn: CACHE_DURATION - age,
      type: 'verbal',
    };
  } catch (error) {
    console.error('Error getting verbal cache info:', error);
    return { cached: false, error: error.message };
  }
};

// Comprehensive cache status for debugging
export const getAllCacheInfo = (userEmail) => {
  const quantInfo = getQuantProgressCacheInfo(userEmail);
  const verbalInfo = getVerbalProgressCacheInfo(userEmail);

  return {
    userEmail,
    quantitative: quantInfo,
    verbal: verbalInfo,
    summary: {
      totalCached: (quantInfo.cached ? 1 : 0) + (verbalInfo.cached ? 1 : 0),
      anyExpired: quantInfo.isExpired || false || verbalInfo.isExpired || false,
      oldestCache:
        quantInfo.cached && verbalInfo.cached
          ? Math.max(quantInfo.age || 0, verbalInfo.age || 0)
          : quantInfo.cached
            ? quantInfo.age
            : verbalInfo.cached
              ? verbalInfo.age
              : 0,
    },
  };
};
